import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import {
  getCachedMarkerIcon,
  getLeadMapPinColor,
} from "../../utils/leadsMapPinIcons";
import { LEADS_MAP_CLUSTER_DESKTOP_OPTS } from "../../utils/leadsMapClustering";

function buildClusterGroup(clusterOptions) {
  const opts = { ...LEADS_MAP_CLUSTER_DESKTOP_OPTS, ...clusterOptions };
  return L.markerClusterGroup({
    disableClusteringAtZoom: opts.disableClusteringAtZoom,
    maxClusterRadius: opts.maxClusterRadius,
    spiderfyOnMaxZoom: true,
    zoomToBoundsOnClick: true,
    showCoverageOnHover: false,
    iconCreateFunction: (cluster) => {
      const count = cluster.getChildCount();
      const size = count < 10 ? 36 : count < 100 ? 40 : 44;
      return L.divIcon({
        className: "tc-leads-cluster-icon",
        html: `<div style="
            width:${size}px;
            height:${size}px;
            border-radius:50%;
            background:#0057A4;
            color:#fff;
            border:2px solid rgba(255,255,255,0.95);
            box-shadow:0 4px 14px rgba(0,0,0,0.28);
            display:flex;
            align-items:center;
            justify-content:center;
            font-weight:700;
            font-size:${count < 100 ? 13 : 12}px;
          ">${count}</div>`,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
      });
    },
  });
}

export default function ClusteredLeadMarkers({
  leads,
  selectedLeads,
  handleMarkerClick,
  drawerLeadId = null,
  clusterOptions,
}) {
  const map = useMap();
  const clusterLayerRef = useRef(null);
  const clusterOptionsRef = useRef(clusterOptions);
  clusterOptionsRef.current = clusterOptions;

  useEffect(() => {
    if (!map || clusterLayerRef.current) return;

    clusterLayerRef.current = buildClusterGroup(clusterOptionsRef.current);
    map.addLayer(clusterLayerRef.current);

    return () => {
      if (clusterLayerRef.current && map.hasLayer(clusterLayerRef.current)) {
        map.removeLayer(clusterLayerRef.current);
      }
      clusterLayerRef.current = null;
    };
  }, [map]);

  useEffect(() => {
    const clusterLayer = clusterLayerRef.current;
    if (!clusterLayer) return;

    clusterLayer.clearLayers();
    const selectedIds = new Set((selectedLeads || []).map((l) => l.id));

    (leads || [])
      .filter((lead) => {
        const lat = lead.latitude || lead.lat;
        const lng = lead.longitude || lead.lng;
        return lat != null && lng != null && !isNaN(lat) && !isNaN(lng);
      })
      .forEach((lead) => {
        const color = getLeadMapPinColor(lead.status, lead.notes);
        const isSelected = selectedIds.has(lead.id);
        const isDrawerSelected =
          !isSelected && drawerLeadId != null && lead.id === drawerLeadId;
        const isDisabled = lead.in_other_rep_route;
        const icon = isDisabled
          ? getCachedMarkerIcon("disabled", color)
          : isSelected || isDrawerSelected
          ? getCachedMarkerIcon("selected", color)
          : getCachedMarkerIcon("normal", color);

        const marker = L.marker([lead.latitude || lead.lat, lead.longitude || lead.lng], {
          icon,
          opacity: isDisabled ? 0.5 : 1,
          title: `${lead.first_name || ""} ${lead.last_name || ""}`.trim(),
        });
        marker.on("click", () => handleMarkerClick(lead));
        clusterLayer.addLayer(marker);
      });
  }, [leads, selectedLeads, handleMarkerClick, drawerLeadId]);

  return null;
}
